import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Alert from 'src/components/atoms/alert'
import AuthTemplate from 'src/components/atoms/authTemplate'
import Button from 'src/components/atoms/button'
import Input from 'src/components/atoms/input'
import NotFound from 'src/components/atoms/notFound'
import Form, { FormItem } from 'src/components/forms'
import useAPIRequest from 'src/hooks/useAPIRequest'
import { routes } from 'src/routes/routes'

import apiClient from '../../apiClient'

export default function ResetPassword() {
    const location = useLocation()
    const navigate = useNavigate()

    const searchParams = new URLSearchParams(location.search)

    const token = searchParams.get('token')
    const username = searchParams.get('username')

    const { callAPI, loading, error, success } = useAPIRequest(apiClient.resetPassword)

    const { t } = useTranslation()

    useEffect(() => {
        if (success) {
            navigate(routes.LOGIN)
            toast.success(t('Your password has been successfully changed.'))
        }
    }, [success])

    const disableCopyPaste = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
    }

    if (!token || !username) {
        return <NotFound />
    }

    return (
        <AuthTemplate>
            <Form
                layout="vertical"
                disabled={loading}
                onFinish={(values) => callAPI({ username, password: values.password, token })}
                className="w-full"
                onCopy={disableCopyPaste}
                onPaste={disableCopyPaste}
                validateTrigger="onSubmit"
            >
                <FormItem
                    className="mb-6"
                    label={t('New password')}
                    name="password"
                    rules={[{ required: true, message: t('Please add new password') }]}
                >
                    <Input autoFocus size="large" type="password" placeholder={t('Enter new password')} />
                </FormItem>

                <FormItem
                    className="mb-6"
                    label={t('Confirm new password')}
                    name="confirmPassword"
                    rules={[
                        { required: true, message: t('Please confirm your password') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error(t('The two passwords that you entered do not match!')))
                            },
                        }),
                    ]}
                >
                    <Input autoFocus size="large" type="password" placeholder={t('Confirm new password')} />
                </FormItem>

                {error ? <Alert message={error} type="error" data-testid="error-reset-password" /> : null}

                <FormItem className="mt-2">
                    <Button htmlType="submit" type="primary" block disabled={loading} loading={loading}>
                        {t('Reset password', 'Reset password')}
                    </Button>
                </FormItem>
            </Form>
        </AuthTemplate>
    )
}
